import { ReactElement, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import BigNumber from 'bignumber.js';

import { TokenIcon } from 'modules/common/components/TokenIcon';
import { DECIMAL_PLACES_SHORT } from 'modules/common/const';
import { MinusIcon, PlusIcon } from 'modules/common/icons';
import { cropString } from 'modules/common/utils/cropString';
import { DeprecatedLiquidStakingRoutesConfig } from 'modules/deprecatedLiquidStaking/Routes';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useGlobalTranslation } from 'modules/i18n';
import { LiquidStakingTab } from 'modules/liquidStaking/const';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { LiquidStakingRoutesConfig } from 'modules/liquidStaking/Routes';

import { StakingTable as Table } from '../StakingTable';
import { translation } from './translation';
import { useStyles } from './useStyles';

export interface ITableRowProps {
  tokenName?: string;
  apy?: string | number;
  amount?: BigNumber;
  amountUsd?: BigNumber;
  tokenAddress?: string;
  isLoading?: boolean;
}

export function TableRow({
  tokenName,
  apy,
  amount,
  amountUsd,
  tokenAddress = '',
  isLoading,
}: ITableRowProps): ReactElement {
  const { keys, t } = useGlobalTranslation(translation);
  const { classes } = useStyles();

  const { isDeprecated } = useLiquidStakingPool();

  const { onOpen } = useDialog<string>(KnownDialogs.lsTabsDialog);

  const onPlusClick = useCallback(
    () => onOpen(LiquidStakingTab.Stake),
    [onOpen],
  );
  const onMinusClick = useCallback(
    () => onOpen(LiquidStakingTab.Unstake),
    [onOpen],
  );

  const withdrawButtonProps = useMemo(() => {
    if (isDeprecated) {
      return {
        component: Link,
        to: DeprecatedLiquidStakingRoutesConfig.dashboard.generatePath(),
      };
    }

    return {
      onClick: onMinusClick,
    };
  }, [isDeprecated, onMinusClick]);

  const tokenCell = (
    <Table.Cell>
      <Stack
        alignItems="center"
        component={Link}
        direction="row"
        spacing={3}
        sx={{ mb: { xs: 4, md: 0 }, color: 'inherit' }}
        to={LiquidStakingRoutesConfig.dashboard.generatePath()}
      >
        {isLoading || !tokenName ? (
          <Skeleton className={classes.poolImg} variant="circular" />
        ) : (
          <TokenIcon className={classes.poolImg} token={tokenName} />
        )}

        <Stack>
          <Typography>
            {!tokenName || isLoading ? <Skeleton width={60} /> : tokenName}
          </Typography>

          <Typography color="text.secondary" variant="body2">
            {!tokenAddress || isLoading ? (
              <Skeleton width={80} />
            ) : (
              cropString(tokenAddress)
            )}
          </Typography>
        </Stack>
      </Stack>
    </Table.Cell>
  );

  const apyCell = (
    <Table.Cell mdLabel={t(keys.apy)}>
      {isLoading ? (
        <Skeleton width={40} />
      ) : (
        t(keys.unit.percent, { value: apy })
      )}
    </Table.Cell>
  );

  const myPositionCell = (
    <Table.Cell mdLabel={t(keys.myPosition)}>
      <Stack alignItems={{ xs: 'end', md: 'start' }}>
        <Typography>
          {isLoading || !amount ? (
            <Skeleton width={60} />
          ) : (
            t(keys.unit.tokenValue, {
              value: amount.decimalPlaces(DECIMAL_PLACES_SHORT).toFormat(),
              token: t(keys.tokens.main),
            })
          )}
        </Typography>

        <Typography color="text.secondary" variant="body2">
          {isLoading ? (
            <Skeleton width={80} />
          ) : (
            amountUsd &&
            t(keys.unit.$, {
              value: amountUsd.toFormat(DECIMAL_PLACES_SHORT),
            })
          )}
        </Typography>
      </Stack>
    </Table.Cell>
  );

  const actionsCell = (
    <Table.Cell>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          display: { md: 'none' },
          width: '100%',
          mt: 3,
        }}
      >
        <Button
          fullWidth
          color="secondary"
          disabled={isLoading}
          variant="outlined"
          {...withdrawButtonProps}
        >
          {t(keys.withdraw)}
        </Button>

        {!isDeprecated && (
          <Button
            fullWidth
            color="primary"
            disabled={isLoading}
            variant="outlined"
            onClick={onPlusClick}
          >
            {t(keys.stakeMore)}
          </Button>
        )}
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={2}
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '100%',
        }}
      >
        <Tooltip placement="top" title={t(keys.withdraw)}>
          <Button
            className={classes.iconButton}
            color="secondary"
            disabled={isLoading}
            variant="outlined"
            {...withdrawButtonProps}
          >
            <MinusIcon />
          </Button>
        </Tooltip>

        {!isDeprecated && (
          <Tooltip placement="top" title={t(keys.stakeMore)}>
            <Button
              className={classes.iconButton}
              color="primary"
              disabled={isLoading}
              variant="outlined"
              onClick={onPlusClick}
            >
              <PlusIcon />
            </Button>
          </Tooltip>
        )}
      </Stack>
    </Table.Cell>
  );

  return (
    <Table.Row>
      {tokenCell}

      {apyCell}

      {myPositionCell}

      {actionsCell}
    </Table.Row>
  );
}
